"use client";
import React from "react";
import Section from "./Section";
import SectionHeader from "./SectionHeader";

import { format, parse } from "date-fns";
import { convertTo12H } from "@/lib/utils";
import { useLocale, useTranslations } from "next-intl";
type AdditionalProps = {
  cancellationPolicy?: "FREE" | "FLEXIBLE" | "STRICT" | "MODERATE";
  petsAllowed?: boolean | "NOT";
  insurance?: string;
  carParkingAvailable?: boolean | "NOT";
  createdAt?: string | "NOT";
  checkInTime?: string;
  checkOutTime?: string;
  refundableInsurance?: boolean;
  title?: string;
  isLoading?: boolean;
};

const AdditionalDetails = ({
  carParkingAvailable,
  createdAt,
  cancellationPolicy,
  insurance,
  petsAllowed,
  checkInTime,
  checkOutTime,
  refundableInsurance,
  title,
  isLoading,
}: AdditionalProps) => {
  const t = useTranslations();
  const formatTime = useGetFormattedTime();

  return (
    <Section className="mb-4 w-full">
      <SectionHeader className={title ? "!text-[#303E48]" : ""}>
        {title ?? t("additionalDetails.header")}
      </SectionHeader>

      <div className="*:flex *:justify-between *:items-center *:px-4 *:py-2.5 *:rounded-2xl space-y-2">
        {createdAt !== "NOT" ? (
          <Row
            isLoading={isLoading}
            name={t("additionalDetails.listedDate")}
            value={createdAt ? format(createdAt, "dd-MM-yyyy") : "------"}
          />
        ) : null}
        <Row
          isLoading={isLoading}
          name={t("additionalDetails.insurance")}
          value={
            refundableInsurance
              ? `${Number(insurance).toLocaleString("en-US")} ${t(
                  "checkout.currency"
                )}`
              : t("additionalDetails.noInsuranceRequired")
          }
        />
        <Row
          isLoading={isLoading}
          name={t("checkout.checkInTime")}
          value={checkInTime ? formatTime(checkInTime) : "------"}
        />
        <Row
          isLoading={isLoading}
          name={t("checkout.checkOutTime")}
          value={checkOutTime ? formatTime(checkOutTime) : "------"}
        />

        <Row
          isLoading={isLoading}
          name={t("checkout.cancellationPolicy")}
          value={
            cancellationPolicy
              ? t(getReschedulingCondition(cancellationPolicy)!)
              : ""
          }
        />
        {petsAllowed !== "NOT" ? (
          <Row
            isLoading={isLoading}
            name={t("additionalDetails.petsAllowed")}
            value={
              petsAllowed
                ? t("additionalDetails.yes")
                : t("additionalDetails.no")
            }
          />
        ) : null}
        {carParkingAvailable !== "NOT" ? (
          <Row
            isLoading={isLoading}
            name={t("additionalDetails.carParkingAvailable")}
            value={
              carParkingAvailable
                ? t("additionalDetails.yes")
                : t("additionalDetails.no")
            }
          />
        ) : null}
      </div>
    </Section>
  );
};

export default AdditionalDetails;

type Props = {
  name: string;
  value: any;
  isLoading?: boolean;
};

const Row = ({ name, value, isLoading }: Props) => {
  return (
    <p className="even:bg-green-background">
      <span className="text-start me-3">{name}</span>
      {isLoading ? (
        <span className="h-6 w-20 skeleton rounded-md dark:bg-neutral-800" />
      ) : (
        <span className="text-end">{value}</span>
      )}
    </p>
  );
};

const getReschedulingCondition = (
  cancellationType: AdditionalProps["cancellationPolicy"]
) => {
  switch (cancellationType) {
    case "FREE":
      return "additionalDetails.reschedule.free";
    case "FLEXIBLE":
      return "additionalDetails.reschedule.flexible";
    case "STRICT":
      return "additionalDetails.reschedule.strict";
    case "MODERATE":
      return "additionalDetails.reschedule.moderate";
  }
};

const useGetFormattedTime = () => {
  const locale = useLocale();

  const formatTime = (date: string) =>
    format(
      parse(
        convertTo12H(new Date(date).toISOString().slice(11, 19)),
        "hh:mm:ss a",
        new Date()
      ),
      "hh:mm a"
    );

  return (date: string) =>
    locale === "ar"
      ? formatTime(date).replace("PM", "مساء").replace("AM", "صباحًا")
      : formatTime(date);
};
